import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Briefcase, LogOut, MessageCircle, User, PlusCircle, Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function Dashboard({ user }) {
  const navigate = useNavigate();
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBusiness();
  }, []);

  const fetchBusiness = async () => {
    try {
      const response = await axios.get(`${API}/businesses`, {
        withCredentials: true,
      });
      const myBusiness = response.data.find((b) => b.user_id === user.user_id);
      setBusiness(myBusiness || null);
    } catch (error) {
      console.error('Error fetching business:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      toast.success('Logged out successfully');
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="dashboard-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50" data-testid="dashboard-header">
        <div className="container mx-auto px-6 md:px-12 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <img src="/s2b-hub-logo.png" alt="The Hub by S2B Group" className="h-12 w-12 object-contain" />
            <div className="flex flex-col leading-none"><span className="text-2xl font-outfit font-bold text-primary">The Hub</span><span className="text-xs text-slate-600 font-normal">by S2B Group</span></div>
          </div>
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/directory')}
              className="text-primary hover:bg-primary/5"
              data-testid="directory-link"
            >
              Directory
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate('/chat')}
              className="text-primary hover:bg-primary/5"
              data-testid="chat-link"
            >
              <MessageCircle className="h-5 w-5 mr-2" />
              Messages
            </Button>
            {user.role === 'admin' && (
              <Button
                variant="ghost"
                onClick={() => navigate('/admin')}
                className="text-accent-orange hover:bg-accent-orange/5"
                data-testid="admin-link"
              >
                Admin
              </Button>
            )}
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="text-primary hover:bg-primary/5"
              data-testid="logout-button"
            >
              <LogOut className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="container mx-auto px-6 md:px-12 py-12">
        <div className="mb-8">
          <h1 className="text-4xl font-outfit font-bold text-primary mb-2">
            Welcome, {user.name}!
          </h1>
          <p className="text-lg text-slate-600">Manage your business profile and connect with others</p>
        </div>

        {loading ? (
          <div className="text-center py-12" data-testid="loading-business">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-orange mx-auto"></div>
            <p className="mt-4 text-slate-600">Loading...</p>
          </div>
        ) : business ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-testid="business-section">
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8">
              <h2 className="text-2xl font-outfit font-semibold text-primary mb-6">Your Business</h2>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-slate-600 mb-1">Business Name</p>
                  <p className="text-lg font-medium text-primary">{business.name}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-600 mb-1">Industry</p>
                  <p className="text-lg text-primary">{business.industry}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-600 mb-1">Years of Experience</p>
                  <p className="text-lg text-primary">{business.years_experience} years</p>
                </div>
                <div>
                  <p className="text-sm text-slate-600 mb-1">Status</p>
                  <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                    business.is_verified ? 'bg-accent-cyan/10 text-accent-cyan' : 'bg-slate-100 text-slate-600'
                  }`}>
                    {business.is_verified ? 'Verified' : 'Pending Verification'}
                  </span>
                </div>
              </div>
              <Button
                onClick={() => navigate('/profile/edit')}
                className="w-full mt-6 bg-accent-orange text-white hover:bg-[#D97000] rounded-full py-3"
                data-testid="edit-profile-button"
              >
                <Settings className="mr-2 h-5 w-5" />
                Edit Profile
              </Button>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8">
              <h2 className="text-2xl font-outfit font-semibold text-primary mb-6">Quick Actions</h2>
              <div className="space-y-4">
                <Button
                  onClick={() => navigate('/directory')}
                  variant="outline"
                  className="w-full justify-start border-slate-200 hover:bg-slate-50 rounded-lg py-6"
                  data-testid="browse-directory-action"
                >
                  <Briefcase className="mr-3 h-5 w-5 text-accent-orange" />
                  <div className="text-left">
                    <p className="font-medium text-primary">Browse Directory</p>
                    <p className="text-sm text-slate-600">Discover other businesses</p>
                  </div>
                </Button>
                <Button
                  onClick={() => navigate('/chat')}
                  variant="outline"
                  className="w-full justify-start border-slate-200 hover:bg-slate-50 rounded-lg py-6"
                  data-testid="messages-action"
                >
                  <MessageCircle className="mr-3 h-5 w-5 text-accent-cyan" />
                  <div className="text-left">
                    <p className="font-medium text-primary">Messages</p>
                    <p className="text-sm text-slate-600">Chat with other businesses</p>
                  </div>
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-12 text-center" data-testid="no-business-section">
            <Briefcase className="h-16 w-16 text-slate-300 mx-auto mb-4" />
            <h2 className="text-2xl font-outfit font-semibold text-primary mb-2">
              No Business Listed Yet
            </h2>
            <p className="text-slate-600 mb-6">
              Create your business listing to start connecting with other entrepreneurs
            </p>
            <Button
              onClick={() => navigate('/profile/edit')}
              className="bg-accent-orange text-white hover:bg-[#D97000] rounded-full px-8 py-3"
              data-testid="create-listing-button"
            >
              <PlusCircle className="mr-2 h-5 w-5" />
              Create Business Listing
            </Button>
          </div>
        )}
      </main>
    </div>
  );
}

export default Dashboard;
